<script setup>
import { computed, inject, nextTick, onMounted, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import Button from 'dashboard/components-next/button/Button.vue';
import JsonEditor from 'flow_builder/components/JsonEditor.vue';
import { getVariablesForTrigger } from 'flow_builder/constants/flowVariables.js';

const { t } = useI18n();
const resolveLabel = label => label?.startsWith('FLOW_BUILDER.') ? t(label) : (label ?? '');

const props = defineProps({
  modelValue: { type: Object, default: () => ({}) },
});
const emit = defineEmits(['update:modelValue']);

const flowTriggerEvent = inject('flowTriggerEvent', null);
const flowDynamicVariables = inject('flowDynamicVariables', ref([]));

const availableVariables = computed(() => {
  const base = getVariablesForTrigger(flowTriggerEvent?.value ?? null);
  return [...base, ...flowDynamicVariables.value];
});

function update(patch) {
  emit('update:modelValue', { ...props.modelValue, ...patch });
}

const METHOD_OPTIONS = ['GET', 'POST'];
const method      = computed(() => props.modelValue?.method ?? 'GET');

const methodTabRefs        = ref([]);
const methodIndicatorStyle = ref({});
const methodEnableTransition = ref(false);

function updateMethodIndicator() {
  nextTick(() => {
    const idx = METHOD_OPTIONS.indexOf(method.value);
    const el  = methodTabRefs.value[idx];
    if (!el) return;
    methodIndicatorStyle.value = { left: `${el.offsetLeft}px`, width: `${el.offsetWidth}px` };
  });
}

watch(method, updateMethodIndicator);
onMounted(() => {
  updateMethodIndicator();
  nextTick(() => { methodEnableTransition.value = true; });
});
const url         = computed(() => props.modelValue?.url ?? '');
const headers     = computed(() => props.modelValue?.headers ?? []);
const body        = computed(() => props.modelValue?.body ?? '');
const varName     = computed(() => props.modelValue?.variable_name ?? '');
const sampleResp  = computed(() => props.modelValue?.sample_response ?? '');

const warningVarPath = computed(() => `variables.${varName.value}`);
const hintVarPath    = computed(() => `{{variables.${varName.value || 'http_response'}.campo}}`);

// Variable picker state
const openPickerField = ref(null);
const pickerSearch    = ref('');

const filteredVars = computed(() => {
  const q = pickerSearch.value.toLowerCase();
  if (!q) return availableVariables.value;
  return availableVariables.value.filter(v => resolveLabel(v.label).toLowerCase().includes(q));
});

function togglePicker(fieldId) {
  openPickerField.value = openPickerField.value === fieldId ? null : fieldId;
  pickerSearch.value = '';
}

function insertVar(fieldId, key) {
  const wrapped = `{{${key}}}`;
  if (fieldId === 'url') {
    update({ url: url.value + wrapped });
  } else if (fieldId === 'body') {
    update({ body: body.value + wrapped });
  } else if (fieldId.startsWith('header-')) {
    const idx = parseInt(fieldId.replace('header-', ''));
    patchHeader(idx, { value: (headers.value[idx]?.value ?? '') + wrapped });
  }
  openPickerField.value = null;
  pickerSearch.value = '';
}

// Headers
function addHeader() {
  update({ headers: [...headers.value, { key: '', value: '' }] });
}

function removeHeader(i) {
  update({ headers: headers.value.filter((_, idx) => idx !== i) });
}

function patchHeader(i, patch) {
  update({ headers: headers.value.map((h, idx) => idx === i ? { ...h, ...patch } : h) });
}

</script>

<template>
  <div class="flex flex-col gap-4">

    <!-- Method -->
    <div class="flex flex-col gap-1.5">
      <span class="text-sm font-medium text-n-slate-12">{{ t('FLOW_BUILDER.ACTION_FORMS.HTTP_REQUEST.METHOD') }}</span>
      <div class="relative flex items-center h-8 rounded-lg bg-n-alpha-1 dark:bg-n-solid-1 w-fit transition-all duration-200 ease-out has-[button:active]:scale-[1.01]">
        <div
          class="absolute rounded-lg bg-n-solid-active shadow-sm pointer-events-none h-8 outline-1 outline outline-n-container inset-y-0"
          :class="{ 'transition-all duration-300 ease-out': methodEnableTransition }"
          :style="methodIndicatorStyle"
        />
        <template v-for="(m, index) in METHOD_OPTIONS" :key="m">
          <button
            :ref="el => (methodTabRefs[index] = el)"
            type="button"
            class="relative z-10 px-6 py-1.5 text-sm border-0 outline-1 outline-transparent rounded-lg transition-all duration-200 ease-out hover:text-n-brand active:scale-[1.02] whitespace-nowrap"
            :class="method === m ? 'text-n-blue-11 scale-100' : 'text-n-slate-10 scale-[0.98]'"
            @click="update({ method: m })"
          >
            {{ m }}
          </button>
          <div
            v-if="index < METHOD_OPTIONS.length - 1"
            class="w-px h-3.5 rounded my-auto transition-colors duration-300 ease-in-out bg-transparent"
          />
        </template>
      </div>
    </div>

    <!-- URL -->
    <div class="flex flex-col gap-1.5">
      <span class="text-sm font-medium text-n-slate-12">{{ t('FLOW_BUILDER.ACTION_FORMS.HTTP_REQUEST.URL') }}</span>
      <div class="relative">
        <div class="flex items-center gap-1 px-2 w-full rounded-lg bg-n-solid-1 outline outline-1 outline-n-weak focus-within:outline-n-brand transition-all">
          <input
            type="text"
            class="reset-base flex-1 min-w-0 h-10 bg-transparent !text-xs !outline-0 text-n-slate-12 placeholder:text-n-slate-9 font-mono"
            :value="url"
            :placeholder="t('FLOW_BUILDER.ACTION_FORMS.HTTP_REQUEST.URL_PLACEHOLDER')"
            @input="update({ url: $event.target.value })"
          />
          <button
            type="button"
            class="shrink-0 inline-flex items-center justify-center size-6 rounded-md text-n-slate-11 hover:bg-n-slate-9/10 hover:text-n-brand transition-colors"
            :title="t('FLOW_BUILDER.COMMON.INSERT_VARIABLE')"
            @click.stop="togglePicker('url')"
          >
            <span class="i-ph-brackets-curly size-3.5 flex-shrink-0" />
          </button>
        </div>
        <div
          v-if="openPickerField === 'url'"
          class="absolute top-full right-0 mt-1 z-[200] bg-n-solid-2 border border-n-strong rounded-lg shadow-lg w-[200px] overflow-hidden"
        >
          <div class="px-2 pt-2 pb-1">
            <div class="flex items-center gap-1.5 px-2 rounded-lg bg-n-alpha-black2 outline outline-1 outline-n-weak focus-within:outline-n-brand">
              <span class="i-lucide-search size-3 text-n-slate-9 shrink-0" />
              <input
                v-model="pickerSearch"
                type="text"
                class="reset-base flex-1 min-w-0 h-7 bg-transparent !text-xs !outline-0 text-n-slate-12 placeholder:text-n-slate-9"
                :placeholder="t('FLOW_BUILDER.COMMON.SEARCH_VARIABLE')"
                autofocus
              />
            </div>
          </div>
          <div class="max-h-[160px] overflow-y-auto py-1 scrollbar-custom">
            <button
              v-for="v in filteredVars"
              :key="v.key"
              type="button"
              class="flex items-center w-full px-3 py-1.5 text-xs text-n-slate-12 hover:bg-n-alpha-2 transition-colors"
              @click="insertVar('url', v.key)"
            >
              {{ resolveLabel(v.label) }}
            </button>
            <p v-if="!filteredVars.length" class="px-3 py-2 text-xs text-n-slate-9 italic">{{ t('FLOW_BUILDER.COMMON.NO_RESULTS') }}</p>
          </div>
        </div>
      </div>
    </div>

    <div class="h-px bg-n-weak -mx-4" />

    <!-- Headers -->
    <div class="flex flex-col gap-2">
      <span class="text-sm font-medium text-n-slate-12">{{ t('FLOW_BUILDER.ACTION_FORMS.HTTP_REQUEST.HEADERS') }}</span>

      <div v-for="(h, i) in headers" :key="i" class="flex items-start gap-1.5">
        <!-- Key -->
        <input
          type="text"
          class="flex-1 min-w-0 h-9 rounded-lg border border-n-weak bg-n-solid-1 px-2.5 text-xs text-n-slate-12 focus:outline-none focus:ring-1 focus:ring-n-brand placeholder:text-n-slate-9 font-mono"
          :placeholder="t('FLOW_BUILDER.ACTION_FORMS.HTTP_REQUEST.HEADER_KEY_PLACEHOLDER')"
          :value="h.key"
          @input="patchHeader(i, { key: $event.target.value })"
        />
        <!-- Value with picker -->
        <div class="relative flex-1 min-w-0">
          <div class="flex items-center gap-1 px-2 w-full rounded-lg bg-n-solid-1 outline outline-1 outline-n-weak focus-within:outline-n-brand transition-all">
            <input
              type="text"
              class="reset-base flex-1 min-w-0 h-10 bg-transparent !text-xs !outline-0 text-n-slate-12 placeholder:text-n-slate-9"
              :placeholder="t('FLOW_BUILDER.ACTION_FORMS.HTTP_REQUEST.HEADER_VALUE_PLACEHOLDER')"
              :value="h.value"
              @input="patchHeader(i, { value: $event.target.value })"
            />
            <button
              type="button"
              class="shrink-0 inline-flex items-center justify-center size-5 rounded-md text-n-slate-11 hover:bg-n-slate-9/10 hover:text-n-brand transition-colors"
              @click.stop="togglePicker(`header-${i}`)"
            >
              <span class="i-ph-brackets-curly size-3 flex-shrink-0" />
            </button>
          </div>
          <div
            v-if="openPickerField === `header-${i}`"
            class="absolute top-full right-0 mt-1 z-[200] bg-n-solid-2 border border-n-strong rounded-lg shadow-lg w-[200px] overflow-hidden"
          >
            <div class="px-2 pt-2 pb-1">
              <div class="flex items-center gap-1.5 px-2 rounded-lg bg-n-alpha-black2 outline outline-1 outline-n-weak focus-within:outline-n-brand">
                <span class="i-lucide-search size-3 text-n-slate-9 shrink-0" />
                <input
                  v-model="pickerSearch"
                  type="text"
                  class="reset-base flex-1 min-w-0 h-7 bg-transparent !text-xs !outline-0 text-n-slate-12 placeholder:text-n-slate-9"
                  :placeholder="t('FLOW_BUILDER.COMMON.SEARCH_VARIABLE')"
                  autofocus
                />
              </div>
            </div>
            <div class="max-h-[160px] overflow-y-auto py-1 scrollbar-custom">
              <button
                v-for="v in filteredVars"
                :key="v.key"
                type="button"
                class="flex items-center w-full px-3 py-1.5 text-xs text-n-slate-12 hover:bg-n-alpha-2 transition-colors"
                @click="insertVar(`header-${i}`, v.key)"
              >
                {{ resolveLabel(v.label) }}
              </button>
              <p v-if="!filteredVars.length" class="px-3 py-2 text-xs text-n-slate-9 italic">{{ t('FLOW_BUILDER.COMMON.NO_RESULTS') }}</p>
            </div>
          </div>
        </div>
        <!-- Remove -->
        <button
          type="button"
          class="shrink-0 inline-flex items-center justify-center size-8 rounded-lg text-n-slate-9 hover:bg-n-ruby-3 hover:text-n-ruby-11 transition-colors mt-1"
          @click="removeHeader(i)"
        >
          <span class="i-lucide-x size-3.5" />
        </button>
      </div>

      <Button ghost slate sm icon="i-lucide-plus" :label="t('FLOW_BUILDER.ACTION_FORMS.HTTP_REQUEST.ADD_HEADER')" class="self-start" @click="addHeader" />
    </div>

    <!-- Body (POST only) -->
    <template v-if="method === 'POST'">
      <div class="h-px bg-n-weak -mx-4" />
      <div class="flex flex-col gap-1.5">
        <span class="text-sm font-medium text-n-slate-12">{{ t('FLOW_BUILDER.ACTION_FORMS.HTTP_REQUEST.BODY_JSON') }}</span>
        <JsonEditor
          :model-value="body"
          @update:model-value="update({ body: $event })"
        />
        <div class="flex justify-end">
          <div class="relative">
            <button
              type="button"
              class="inline-flex items-center justify-center gap-1.5 px-2.5 h-7 rounded-lg text-xs text-n-slate-11 hover:bg-n-slate-9/10 hover:text-n-brand transition-colors border border-n-weak"
              @click.stop="togglePicker('body')"
            >
              <span class="i-ph-brackets-curly size-3.5 flex-shrink-0" />
              {{ t('FLOW_BUILDER.COMMON.INSERT_VARIABLE') }}
            </button>
            <div
              v-if="openPickerField === 'body'"
              class="absolute bottom-full right-0 mb-1 z-[200] bg-n-solid-2 border border-n-strong rounded-lg shadow-lg w-[200px] overflow-hidden"
            >
              <div class="px-2 pt-2 pb-1">
                <div class="flex items-center gap-1.5 px-2 rounded-lg bg-n-alpha-black2 outline outline-1 outline-n-weak focus-within:outline-n-brand">
                  <span class="i-lucide-search size-3 text-n-slate-9 shrink-0" />
                  <input
                    v-model="pickerSearch"
                    type="text"
                    class="reset-base flex-1 min-w-0 h-7 bg-transparent !text-xs !outline-0 text-n-slate-12 placeholder:text-n-slate-9"
                    :placeholder="t('FLOW_BUILDER.COMMON.SEARCH_VARIABLE')"
                    autofocus
                  />
                </div>
              </div>
              <div class="max-h-[160px] overflow-y-auto py-1 scrollbar-custom">
                <button
                  v-for="v in filteredVars"
                  :key="v.key"
                  type="button"
                  class="flex items-center w-full px-3 py-1.5 text-xs text-n-slate-12 hover:bg-n-alpha-2 transition-colors"
                  @click="insertVar('body', v.key)"
                >
                  {{ resolveLabel(v.label) }}
                </button>
                <p v-if="!filteredVars.length" class="px-3 py-2 text-xs text-n-slate-9 italic">{{ t('FLOW_BUILDER.COMMON.NO_RESULTS') }}</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </template>

    <div class="h-px bg-n-weak -mx-4" />

    <!-- Sample response -->
    <div class="flex flex-col gap-1.5">
      <span class="text-sm font-medium text-n-slate-12">{{ t('FLOW_BUILDER.ACTION_FORMS.HTTP_REQUEST.SAMPLE_RESPONSE') }}</span>
      <p class="text-xs text-n-slate-9">{{ t('FLOW_BUILDER.ACTION_FORMS.HTTP_REQUEST.SAMPLE_RESPONSE_HINT') }}</p>
      <p v-if="varName && !sampleResp" class="text-xs text-n-amber-11 flex items-center gap-1">
        <span class="i-lucide-triangle-alert size-3 shrink-0" />
        {{ t('FLOW_BUILDER.ACTION_FORMS.HTTP_REQUEST.NO_SAMPLE_WARNING_BEFORE') }} <code class="font-mono">{{ warningVarPath }}</code> {{ t('FLOW_BUILDER.ACTION_FORMS.HTTP_REQUEST.NO_SAMPLE_WARNING_AFTER') }}
      </p>
      <JsonEditor
        :model-value="sampleResp"
        @update:model-value="update({ sample_response: $event })"
      />
    </div>

    <!-- Variable name -->
    <div class="flex flex-col gap-1.5">
      <span class="text-sm font-medium text-n-slate-12">{{ t('FLOW_BUILDER.ACTION_FORMS.HTTP_REQUEST.SAVE_RESPONSE_IN_VARIABLE') }}</span>
      <input
        type="text"
        class="w-full h-9 rounded-lg border border-n-weak bg-n-solid-1 px-3 text-sm text-n-slate-12 focus:outline-none focus:ring-1 focus:ring-n-brand placeholder:text-n-slate-9 font-mono"
        :placeholder="t('FLOW_BUILDER.ACTION_FORMS.HTTP_REQUEST.VARIABLE_NAME_PLACEHOLDER')"
        :value="varName"
        @input="update({ variable_name: $event.target.value })"
      />
      <p class="text-xs text-n-slate-9">
        {{ t('FLOW_BUILDER.ACTION_FORMS.HTTP_REQUEST.VARIABLE_HINT_BEFORE') }} <code class="text-n-slate-11">{{ hintVarPath }}</code> {{ t('FLOW_BUILDER.ACTION_FORMS.HTTP_REQUEST.VARIABLE_HINT_AFTER') }}
      </p>
    </div>

    <!-- Retry -->
    <div class="h-px bg-n-weak -mx-4" />
    <div class="flex flex-col gap-3">
      <span class="text-sm font-medium text-n-slate-12">{{ t('FLOW_BUILDER.ACTION_FORMS.HTTP_REQUEST.RETRY_AUTOMATIC') }}</span>
      <div class="flex gap-3">
        <div class="flex flex-col gap-1.5 flex-1">
          <span class="text-xs text-n-slate-9">{{ t('FLOW_BUILDER.ACTION_FORMS.HTTP_REQUEST.RETRY_EXTRA_ATTEMPTS') }}</span>
          <input
            type="number"
            min="0"
            max="5"
            class="w-full h-9 rounded-lg border border-n-weak bg-n-solid-1 px-2.5 text-sm text-n-slate-12 focus:outline-none focus:ring-1 focus:ring-n-brand"
            :value="modelValue?.retry_count ?? 0"
            @input="update({ retry_count: parseInt($event.target.value) || 0 })"
          />
        </div>
        <div class="flex flex-col gap-1.5 flex-1">
          <span class="text-xs text-n-slate-9">{{ t('FLOW_BUILDER.ACTION_FORMS.HTTP_REQUEST.RETRY_INTERVAL_SECONDS') }}</span>
          <input
            type="number"
            min="1"
            max="30"
            class="w-full h-9 rounded-lg border border-n-weak bg-n-solid-1 px-2.5 text-sm text-n-slate-12 focus:outline-none focus:ring-1 focus:ring-n-brand"
            :value="modelValue?.retry_delay_seconds ?? 5"
            @input="update({ retry_delay_seconds: parseInt($event.target.value) || 5 })"
          />
        </div>
      </div>
    </div>

    <!-- Outputs -->
    <div class="flex flex-col gap-1.5 p-3 rounded-lg bg-n-alpha-1 border border-n-weak">
      <div class="flex items-center gap-2 text-xs text-n-slate-9">
        <span class="inline-block size-2 rounded-full bg-n-teal-9 shrink-0" />
        <span><strong class="text-n-slate-12">{{ t('FLOW_BUILDER.ACTION_FORMS.HTTP_REQUEST.OUTPUT_SUCCESS_LABEL') }}</strong> {{ t('FLOW_BUILDER.ACTION_FORMS.HTTP_REQUEST.OUTPUT_SUCCESS_DESC') }}</span>
      </div>
      <div class="flex items-center gap-2 text-xs text-n-slate-9">
        <span class="inline-block size-2 rounded-full bg-n-ruby-9 shrink-0" />
        <span><strong class="text-n-slate-12">{{ t('FLOW_BUILDER.ACTION_FORMS.HTTP_REQUEST.OUTPUT_ERROR_LABEL') }}</strong> {{ t('FLOW_BUILDER.ACTION_FORMS.HTTP_REQUEST.OUTPUT_ERROR_DESC') }}</span>
      </div>
    </div>

  </div>
</template>
